import type * as React from 'react';
import { createFileRoute, Link as RouterLink } from '@tanstack/react-router';
import {
  Alert,
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Link as MuiLink,
  Stack,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {
  Apartment,
  Campaign,
  ChevronRight,
  FactCheck,
  Flag,
  Groups,
  LocalOffer,
  MonitorHeart,
  Paid,
  Payments,
  ReceiptLong,
  Schedule,
  Settings,
  VerifiedUser,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import * as admin from '@/api/admin';
import type { ServiceHealthStatus } from '@/api/admin';
import * as metricsApi from '@/api/metrics';
import { usePermissions } from '@/hooks/usePermissions';

const LinkAny = RouterLink as unknown as React.ElementType;

/**
 * Panel de plataforma (`/admin`) — home del back-office de SuperAdmin. Consolida en una sola
 * pantalla el "pulso" que antes estaba disperso: KPIs de negocio (antes en `/dashboard/metrics`),
 * snapshot de salud de servicios (resumen de `/admin/estado`) y conteo de cuentas, más accesos
 * rápidos a las cuatro secciones del back-office. Es la landing al entrar al producto Platform Admin.
 */
export const Route = createFileRoute('/_authenticated/admin/')({
  component: AdminPanel,
});

const usdFormat = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

const HEALTH: Record<ServiceHealthStatus, { label: string; color: 'success' | 'warning' | 'error' }> = {
  Operational: { label: 'Operativo', color: 'success' },
  Degraded: { label: 'Degradado', color: 'warning' },
  Outage: { label: 'Caído', color: 'error' },
};

const SECTIONS: { to: string; icon: JSX.Element; label: string; caption: string }[] = [
  { to: '/admin/organizaciones', icon: <Apartment fontSize="small" />, label: 'Organizaciones', caption: 'Cuentas, productos e impersonación' },
  { to: '/admin/suscripciones', icon: <Paid fontSize="small" />, label: 'Suscripciones', caption: 'Billing, MRR y cola de dunning' },
  { to: '/admin/cupones', icon: <LocalOffer fontSize="small" />, label: 'Cupones', caption: 'Descuentos y promociones' },
  { to: '/admin/estudios-amigos', icon: <Groups fontSize="small" />, label: 'Estudios Amigos', caption: 'Invitaciones bonificadas y feedback piloto' },
  { to: '/admin/comprobantes', icon: <ReceiptLong fontSize="small" />, label: 'Comprobantes', caption: 'Vista de soporte cross-tenant' },
  { to: '/admin/pagos', icon: <Payments fontSize="small" />, label: 'Tilde Pagos', caption: 'Auditoría técnica y consumo de API' },
  { to: '/admin/auditoria', icon: <FactCheck fontSize="small" />, label: 'Auditoría', caption: 'Acciones, logins e impersonaciones' },
  { to: '/admin/certificados', icon: <VerifiedUser fontSize="small" />, label: 'Certificados', caption: 'ARCA por vencer cross-tenant' },
  { to: '/admin/estado', icon: <MonitorHeart fontSize="small" />, label: 'Estado & Incidentes', caption: 'Salud de servicios e incidentes' },
  { to: '/admin/workers', icon: <Schedule fontSize="small" />, label: 'Workers', caption: 'Jobs de fondo + trigger manual' },
  { to: '/admin/anuncios', icon: <Campaign fontSize="small" />, label: 'Anuncios', caption: 'Banner de mantenimiento a tenants' },
  { to: '/admin/feature-flags', icon: <Flag fontSize="small" />, label: 'Feature flags', caption: 'Prender/apagar features' },
  { to: '/admin/configuracion', icon: <Settings fontSize="small" />, label: 'Configuración', caption: 'Planes, integraciones y seguridad' },
];

function Kpi({ label, value, hint }: { label: string; value: string; hint?: string }): JSX.Element {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="overline" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          {value}
        </Typography>
        {hint ? (
          <Typography variant="caption" color="text.secondary">
            {hint}
          </Typography>
        ) : null}
      </CardContent>
    </Card>
  );
}

function AdminPanel(): JSX.Element {
  const { canViewMetrics } = usePermissions();

  const metricsQ = useQuery({
    queryKey: ['metrics', 'business'],
    queryFn: metricsApi.getBusinessMetrics,
    enabled: canViewMetrics,
  });
  const healthQ = useQuery({
    queryKey: ['service-health'],
    queryFn: admin.getServiceHealth,
    refetchInterval: 60_000,
  });
  const orgsQ = useQuery({ queryKey: ['organizations'], queryFn: () => admin.listOrganizations(0, 200) });

  const m = metricsQ.data;
  const report = healthQ.data;
  const orgTotal = orgsQ.data?.total ?? orgsQ.data?.items.length;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
        Panel de plataforma
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        El pulso del negocio y la operación de Tilde.
      </Typography>

      {/* KPIs de negocio (antes en /dashboard/metrics) */}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Kpi label="MRR" value={m ? usdFormat.format(m.mrr) : '—'} hint="Suscripciones activas" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Kpi label="Nuevos tenants (30d)" value={m ? String(m.newTenantsLast30Days) : '—'} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Kpi label="Churn (30d)" value={m ? String(m.churnedLast30Days) : '—'} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Kpi
            label="Organizaciones"
            value={orgTotal != null ? String(orgTotal) : '—'}
            hint="Cuentas en la plataforma"
          />
        </Grid>
      </Grid>

      {!canViewMetrics ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          Las métricas de negocio (MRR, churn) requieren rol Operator o SuperAdmin.
        </Alert>
      ) : null}

      <Grid container spacing={2} sx={{ mt: 1 }}>
        {/* Snapshot de salud de servicios (resumen de /admin/estado) */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Salud de servicios
                </Typography>
                <MuiLink component={LinkAny} to="/admin/estado" variant="caption" sx={{ fontWeight: 600 }}>
                  Ver Estado →
                </MuiLink>
              </Box>
              {report ? (
                <>
                  <Chip
                    size="small"
                    color={HEALTH[report.overall].color}
                    label={`General: ${HEALTH[report.overall].label}`}
                    sx={{ mb: 1.5 }}
                  />
                  <Stack spacing={1}>
                    {report.services.map((s) => (
                      <Box
                        key={s.service}
                        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                      >
                        <Typography variant="body2">{s.service}</Typography>
                        <Chip
                          size="small"
                          variant="outlined"
                          color={HEALTH[s.status].color}
                          label={HEALTH[s.status].label}
                        />
                      </Box>
                    ))}
                  </Stack>
                </>
              ) : healthQ.isError ? (
                <Alert severity="error">No pudimos cargar la salud de servicios.</Alert>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Cargando…
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Accesos rápidos a las secciones del back-office */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                Accesos rápidos
              </Typography>
              <Stack spacing={1}>
                {SECTIONS.map((s) => (
                  <Card key={s.to} variant="outlined">
                    <CardActionArea
                      component={LinkAny}
                      to={s.to}
                      sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, justifyContent: 'flex-start' }}
                    >
                      <Box sx={{ display: 'flex', color: 'action.active' }}>{s.icon}</Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {s.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {s.caption}
                        </Typography>
                      </Box>
                      <ChevronRight fontSize="small" color="action" />
                    </CardActionArea>
                  </Card>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
